import React, { FC, ReactNode } from 'react';

import { Box, Grid, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { ArrowRight } from 'phosphor-react';

import useAssetPrices from '~components/hooks/protocol/assets/useAssetPrices';
import Link from '~components/shared/link';
import {
  primaryColors,
  secondaryColors,
  statusGreen,
  statusRed,
  vestaGradient,
} from '~components/theme/styles/colors';
import { EDisplayType } from '~components/types';
import { displayFormatUnits } from '~components/utils/utils';
import { TOKEN_BUY_LINKS } from '~config/links';
import { AllVestaAssets, BoostedFarm, ENativeAsset, EVestaAsset } from '~config/types';

import { GenericInfoLabel, GenericInfoLabelProps, GenericInfoLabelToolTip } from '..';
import { TransactionButton } from '../../buttons/transactionButton';
import { AssetIcon } from '../../icons/assets';
import { OverlappedAssetIcons } from '../../icons/assets/overlapped';
import { VestaIcon, VestaIcons } from '../../icons/vesta';
import { LabeledSwitch } from '../../switch';

export type OptionalGenericInfoLabelProps = Omit<
  GenericInfoLabelProps,
  'labelTitle' | 'labelValue' | 'labelValueAnnotation'
>;

export type LabelStatVariant = 'body' | 'header';

export const LabelPoolRewards: FC<{
  rewards: AllVestaAssets[];
  variant?: LabelStatVariant;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ rewards, variant = 'body', labelProps }) => {
  return (
    <Stack>
      <GenericInfoLabel
        {...labelProps}
        variant={variant === 'body' ? 'horizontal' : 'vertical'}
        labelValueVariant={variant === 'body' ? 'label3' : 'label2'}
        labelTitle={'REWARDS'}
        labelValue={rewards[0]}
        labelValueUnit={<AssetIcon size={16} noBg noBorder asset={rewards[0]} />}
      />
    </Stack>
  );
  // );
};

export const LabelBigEstimatedRewards: FC<{
  estimatedRewards: number;
  rewardAsset: AllVestaAssets;
  lockPeriod?: number;
}> = ({ estimatedRewards, rewardAsset, lockPeriod }) => {
  return (
    <Stack sx={{ flexDirection: 'column', gap: '4px' }}>
      {lockPeriod !== undefined ? (
        <Typography sx={{ color: primaryColors[600] }} variant="label3">
          YOUR ESTIMATED REWARDS FOR A{' '}
          <Box component="span" sx={{ color: primaryColors[100] }}>
            {lockPeriod} DAYS{' '}
          </Box>
          LOCK PERIOD
        </Typography>
      ) : (
        <Typography sx={{ color: primaryColors[600] }} variant="label3">
          YOUR ESTIMATED REWARDS{' '}
        </Typography>
      )}
      <UserPoolRewards
        noClaimButton
        rewards={[{ amount: estimatedRewards, asset: rewardAsset }]}
        noLabel
        disabled
      />
    </Stack>
  );
};

export const LabelWithPrice: React.FC<{
  asset?: AllVestaAssets;
  assetAmount: number;
  assetAmountColor?: string;
  labelTitle?: string;
  children?: ReactNode;
  labelProps?: OptionalGenericInfoLabelProps;
  halfVertical?: boolean;
  verticalOnMobile?: boolean;
}> = ({
  asset,
  halfVertical,
  assetAmount,
  assetAmountColor = primaryColors[100],
  labelTitle,
  labelProps = { variant: 'vertical' },
  verticalOnMobile,
}) => {
  const {
    data: { all },
  } = useAssetPrices();

  const assetPrice = asset ? all?.[asset] : 0;

  return (
    <GenericInfoLabel
      labelValueSx={{ color: assetAmountColor }}
      {...labelProps}
      labelTitle={labelTitle}
      labelValue={displayFormatUnits({
        value: assetAmount,
        displayType: EDisplayType.number,
        decimal: 2,
        unit: asset,
      })}
      labelValueUnitSx={halfVertical ? { color: primaryColors[600] } : undefined}
      labelValueUnit={
        halfVertical
          ? `(${displayFormatUnits({
              value: assetPrice * assetAmount,
              displayType: EDisplayType.USD,
            })})`
          : undefined
      }
      verticalOnMobile={verticalOnMobile}
      labelValueAnnotation={
        !halfVertical && !isNaN(assetAmount) && asset
          ? labelProps.variant === 'vertical'
            ? displayFormatUnits({
                value: assetPrice * assetAmount,
                displayType: EDisplayType.USD,
              })
            : `(${displayFormatUnits({
                value: assetPrice * assetAmount,
                displayType: EDisplayType.USD,
              })})`
          : undefined
      }
    />
  );
};

export const LabelApr: React.FC<{
  apr: number;
  rewards?: AllVestaAssets[];
  customTitle?: string;
  variant?: LabelStatVariant;
  labelProps?: OptionalGenericInfoLabelProps;
  iconSize?: number;
  hasTooltip?: boolean;
}> = ({
  iconSize = 16,
  apr,
  customTitle,
  rewards,
  variant = 'body',
  labelProps = {
    variant: variant === 'body' ? 'horizontal' : 'vertical',
    align: variant === 'body' ? 'start' : 'end',
  },
  hasTooltip = true,
}) => (
  <GenericInfoLabel
    toolTip={
      hasTooltip ? { content: 'APR For current pool.', placement: rewards ? 'title' : 'value' } : undefined
    }
    labelTitle={customTitle ?? (rewards ? `${rewards?.[0]} APR` : 'APR')}
    labelValue={apr === 0 ? '-' : displayFormatUnits({ value: apr, displayType: EDisplayType.Percent })}
    labelValueUnit={
      rewards ? <OverlappedAssetIcons noBg noBorder iconSize={iconSize} assets={rewards} /> : undefined
    }
    {...labelProps}
  />
);

export const LabelPoolShare: React.FC<{
  poolShare: number;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({
  poolShare,
  labelProps = { toolTip: { content: 'Your current pool share', placement: 'value' } },
}) => (
  <GenericInfoLabel
    {...labelProps}
    labelTitle="POOL SHARE:"
    labelValue={displayFormatUnits({ value: poolShare, displayType: EDisplayType.Percent })}
  />
);

export const LabelNewPoolShare: React.FC<{
  poolShare: number;
  isStake?: boolean;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ poolShare, isStake = true, labelProps }) => (
  <GenericInfoLabel
    {...labelProps}
    labelTitle="NEW POOL SHARE:"
    labelValueSx={{ color: isStake ? statusGreen[100] : statusRed[100] }}
    labelValueVariant={'label3'}
    labelValue={
      poolShare >= 0.999
        ? '~ 100.00 %'
        : displayFormatUnits({ value: poolShare, displayType: EDisplayType.Percent })
    }
    toolTip={{ content: 'Your new pool share after your action', placement: 'title' }}
  />
);

export const LabelStakedAmount: React.FC<{
  asset: AllVestaAssets;
  stakedAmount: number;
  variant?: LabelStatVariant;
  customTitle?: string;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({
  asset,
  stakedAmount,
  variant = 'header',
  customTitle,
  labelProps = {
    align: 'start',
    variant: variant === 'header' ? 'vertical' : 'horizontal',
    labelValueVariant: variant === 'header' ? 'label2' : 'label3',
    toolTip: {
      content: 'Your staked tokens',
      placement: variant === 'header' ? 'title' : 'value',
    },
  },
}) => (
  <LabelWithPrice
    labelProps={labelProps}
    labelTitle={customTitle ? customTitle : 'STAKED AMOUNT'}
    assetAmount={stakedAmount}
    asset={asset}
  />
);

export const LabelNewStakedAmount: React.FC<{
  asset: AllVestaAssets;
  stakedAmount: number;
  stakedAmountUsd: number;
  isStake?: boolean;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ asset, stakedAmount, stakedAmountUsd, isStake = true, labelProps = { align: 'end' } }) => (
  <GenericInfoLabel
    {...labelProps}
    labelTitle="NEW STAKED AMOUNT:"
    labelValueSx={{ color: isStake ? statusGreen[100] : statusRed[100] }}
    labelValueVariant={'label3'}
    labelValue={displayFormatUnits({
      value: stakedAmount,
      displayType: EDisplayType.Token,
      decimal: 5,
      unit: asset,
    })}
    labelValueAnnotation={`(${displayFormatUnits({
      value: stakedAmountUsd,
      displayType: EDisplayType.USD,
    })})`}
  />
);

export const LabelTotalValueLocked: React.FC<{
  totalValueLocked: number;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ totalValueLocked, labelProps = { align: 'end' } }) => (
  <GenericInfoLabel
    {...labelProps}
    labelTitle="TVL:"
    labelValue={displayFormatUnits({ value: totalValueLocked, displayType: EDisplayType.USD })}
    toolTip={{ content: 'Total Value Locked in current pool.', placement: 'value' }}
  />
);

export const LabelLiquidationBonusStat: React.FC<{
  liquidationBonus: number;
  asset?: EVestaAsset;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ liquidationBonus, labelProps, asset }) => (
  <GenericInfoLabel
    {...labelProps}
    labelTitle="LIQUIDATION BONUS:"
    labelValue={displayFormatUnits({ value: liquidationBonus, displayType: EDisplayType.Percent })}
    toolTip={{
      content: 'The bonus Vesta provides on all current vault liquidation rewards.',
      placement: 'title',
    }}
    skeletonProps={labelProps?.labelValueVariant === 'label1' ? { height: 32 } : undefined}
    labelValueUnit={
      asset ? (
        <AssetIcon size={labelProps?.labelValueVariant === 'label1' ? 20 : 14} noBg noBorder asset={asset} />
      ) : undefined
    }
  />
);

export const LabelTotalLiquidationBonus: React.FC<{
  asset: EVestaAsset;
  totalLiquidationBonus: number;
  totalLiquidationBonusUsd: number;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ asset, totalLiquidationBonus, totalLiquidationBonusUsd, labelProps = { align: 'end' } }) => (
  <GenericInfoLabel
    {...labelProps}
    labelTitle="TOTAL BONUSES:"
    labelValueVariant={'label3'}
    labelValue={displayFormatUnits({
      value: totalLiquidationBonus,
      displayType: EDisplayType.number,
      decimal: 5,
      unit: asset,
    })}
    labelValueAnnotation={`(${displayFormatUnits({
      value: totalLiquidationBonusUsd,
      displayType: EDisplayType.USD,
    })})`}
  />
);

export const LabelLiquidationGains: React.FC<{
  asset: EVestaAsset;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ asset, labelProps = { align: 'end' } }) => (
  <GenericInfoLabel
    {...labelProps}
    labelTitle="LIQ. GAINS"
    labelValueVariant={'label3'}
    labelValue={`$${asset}`}
    labelValueUnit={<AssetIcon size={14} noBg noBorder asset={asset} />}
    toolTip={{ content: 'The asset you receive when liquidations occur.', placement: 'title' }}
  />
);

export const LabelStakeUnixDate: React.FC<{
  stakeDate: number;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ stakeDate, labelProps }) => (
  <GenericInfoLabel
    {...labelProps}
    labelTitle="STAKE DATE:"
    labelValue={moment(stakeDate).format('MMMM Do YYYY')}
    toolTip={{ content: 'lorem ipsum', placement: 'value' }}
  />
);

export const LabelLockPeriodDays: React.FC<{
  lockPeriod: number;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ lockPeriod, labelProps = { labelValueSx: { color: primaryColors[300] } } }) => (
  <GenericInfoLabel
    {...labelProps}
    labelTitle="LOCK PERIOD:"
    labelValue={`${lockPeriod} DAYS`}
    toolTip={{ content: 'LOREM IPSUM CMON', placement: 'value' }}
  />
);

export const LabelUnlockDate: React.FC<{
  withdrawDate: number;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ withdrawDate, labelProps }) => (
  <GenericInfoLabel
    {...labelProps}
    labelTitle="UNLOCK DATE:"
    labelValue={moment(withdrawDate).format('MMMM Do YYYY')}
    toolTip={{ content: 'LOREM IPSUM YOOOO', placement: 'value' }}
  />
);

export const LabelDaysPassed: React.FC<{
  daysPassed: number | string;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ daysPassed, labelProps }) => (
  <GenericInfoLabel {...labelProps} labelTitle="DAYS PASSED:" labelValue={`${daysPassed} DAYS`} />
);

export const UserPoolRewards: React.FC<{
  rewards: { asset?: AllVestaAssets; amount: number }[];
  title?: string;
  total?: boolean;
  noLabel?: boolean;
  onClaim?: () => void;
  txKey?: string;
  noClaimButton?: boolean;
  horizontal?: boolean;
  disabled?: boolean;
  labelProps?: OptionalGenericInfoLabelProps;
  width?: string;
}> = ({
  rewards,
  noLabel,
  horizontal,
  disabled,
  onClaim,
  total,
  txKey,
  title,
  noClaimButton,
  labelProps,
  width,
}) => {
  const totalClaimable = rewards.reduce((prev, cur) => prev + cur.amount, 0);
  return (
    <Stack justifyContent={'space-between'} direction={'row'} alignItems={'center'} width={width}>
      <GenericInfoLabel
        labelTitle={!noLabel ? (title ? title : total ? 'TOTAL PENDING REWARDS: ' : 'REWARDS: ') : ''}
        labelValue={rewards.map((reward, index) => (
          <React.Fragment key={index}>
            {reward.amount > 0 ? (
              <Stack direction={'row'} spacing={1}>
                <Typography
                  key={index}
                  sx={{
                    background: vestaGradient,
                    backgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                  typography={labelProps?.labelValueVariant ?? 'label2'}
                  component="span"
                >
                  {`${reward.asset ? '' : '$'}${displayFormatUnits({
                    value: reward.amount,
                    displayType: EDisplayType.Token,
                  })} `}
                </Typography>
                {reward.asset ? (
                  <Typography
                    component="span"
                    color={primaryColors[400]}
                    typography={labelProps?.labelValueVariant ?? 'label2'}
                  >
                    {reward.asset}
                  </Typography>
                ) : (
                  ''
                )}
              </Stack>
            ) : index < 1 ? (
              <Typography key={index} component="span" typography={labelProps?.labelValueVariant ?? 'label2'}>
                N/A
              </Typography>
            ) : (
              ''
            )}
          </React.Fragment>
        ))}
        labelValueSx={{ color: secondaryColors[200] }}
        variant={horizontal ? 'horizontal' : 'vertical'}
        {...labelProps}
      />
      {!noClaimButton && txKey ? (
        <TransactionButton
          iconType="coin"
          sx={{
            minWidth: 42,
            width: 42,
            maxWidth: 42,
            p: 1,
            '&:disabled': { background: 'initial' },
          }}
          variant={'primary'}
          txKey={txKey!}
          confirmContent={' '}
          sendingContent={' '}
          successContent={' '}
          failedContent={' '}
          onClick={onClaim}
          disabled={rewards.length <= 0 || totalClaimable <= 0 || disabled}
        />
      ) : null}
    </Stack>
  );
};

export const LabelOverviewUserRewards: React.FC<{
  asset: AllVestaAssets;
  rewardAmount: number;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ asset, rewardAmount, labelProps }) => {
  return (
    <GenericInfoLabel
      {...labelProps}
      labelTitle={'PENDING REWARDS:'}
      labelValue={displayFormatUnits({
        value: rewardAmount,
        displayType: EDisplayType.number,
        decimal: 5,
        unit: asset,
      })}
      labelValueUnit={<AssetIcon noBg noBorder size={14} asset={asset} />}
    />
  );
};
export const LabelRewardsAccumulated: React.FC<{
  asset: AllVestaAssets;
  rewardAmount: number;
  rewardAmountUsd: number;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ asset, rewardAmount, rewardAmountUsd, labelProps }) => {
  return (
    <GenericInfoLabel
      {...labelProps}
      labelTitle={'PENDING REWARDS:'}
      labelValue={displayFormatUnits({
        value: rewardAmount,
        displayType: EDisplayType.number,
        decimal: 5,
        unit: asset,
      })}
      labelValueAnnotation={`(${displayFormatUnits({
        value: rewardAmountUsd,
        displayType: EDisplayType.USD,
      })})`}
    />
  );
};

export const LabelMarketPrice: React.FC<{
  asset: AllVestaAssets;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ asset, labelProps }) => {
  const { data: priceData } = useAssetPrices();
  return (
    <GenericInfoLabel
      {...labelProps}
      labelTitle="MARKET PRICE:"
      labelValue={displayFormatUnits({
        value: priceData.market[asset === EVestaAsset.ETH ? 'WETH' : asset],
        displayType: EDisplayType.USD,
      })}
    />
  );
};

export const LabelOraclePrice: React.FC<{
  asset: EVestaAsset;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ asset, labelProps }) => {
  const { data: priceData } = useAssetPrices();
  return (
    <GenericInfoLabel
      {...labelProps}
      labelTitle="ORACLE PRICE:"
      labelValue={displayFormatUnits({
        value: priceData.oracle[asset],
        displayType: EDisplayType.USD,
      })}
    />
  );
};

export const LabelCumulativeAmountUSD: React.FC<{
  labelTitle: string;
  value: number;
  isLoading: boolean;
  tooltipContent: string;
}> = ({ value, tooltipContent, labelTitle, isLoading }) => {
  return (
    <GenericInfoLabel
      variant="vertical"
      labelTitle={labelTitle}
      labelValueVariant={'label2'}
      isLoading={isLoading}
      labelValue={displayFormatUnits({ value: value, displayType: EDisplayType.USD })}
      toolTip={{ content: tooltipContent, placement: 'title' }}
    />
  );
};

export const LabelEstimatedAPR: React.FC<{
  approxApr: number;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ labelProps = { variant: 'vertical', align: 'right' }, approxApr }) => {
  return (
    <GenericInfoLabel
      {...labelProps}
      labelTitle="EST. APR:"
      labelValue={`~${displayFormatUnits({ value: approxApr, displayType: EDisplayType.Percent })}`}
      labelValueVariant={'label2'}
    />
  );
};

export const LabelRewards: React.FC<{
  rewards: number;
  asset: AllVestaAssets;
  title?: string;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ labelProps, rewards, asset, title = 'REWARDS' }) => {
  return (
    <GenericInfoLabel
      {...labelProps}
      labelTitle={title}
      labelValue={
        <Typography variant="label2">
          <Box component="span" sx={{ color: secondaryColors[200] }}>
            {`${displayFormatUnits({
              value: rewards,
              decimal: 5,
              displayType: EDisplayType.number,
            })} `}
          </Box>
          {asset}
        </Typography>
      }
      toolTip={{
        placement: 'title',
        content: `Accumulated ${asset} rewards`,
      }}
    />
  );
};

export const LabelLiquidationRewards: React.FC<{
  liquidationGains: number;
  asset: AllVestaAssets;
  title?: string;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ labelProps, liquidationGains, asset, title = 'LIQUIDATION GAINS' }) => {
  return (
    <GenericInfoLabel
      {...labelProps}
      labelTitle={title}
      skeletonProps={{ height: 32 }}
      labelValue={
        <Typography variant="label2">
          <Box component="span" sx={{ color: secondaryColors[200] }}>
            {`${displayFormatUnits({
              value: liquidationGains,
              decimal: 5,
              displayType: EDisplayType.number,
            })} `}
          </Box>
          {asset}
        </Typography>
      }
      toolTip={{
        placement: 'title',
        content: `Accumulated rewards from liquidation`,
      }}
    />
  );
};

export const LabelRewardApproximation: React.FC<{
  approximation: number;
  asset: AllVestaAssets;
  title?: string;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ labelProps, approximation, asset, title = 'FRIENDLY REWARDS APPROXIMATION' }) => {
  return (
    <GenericInfoLabel
      {...labelProps}
      labelTitle={title}
      variant="vertical"
      labelValue={
        <Typography variant="label2">
          <Box component="span" sx={{ color: secondaryColors[200] }}>
            {`${displayFormatUnits({
              value: approximation,
              decimal: 5,
              displayType: EDisplayType.number,
            })} `}
          </Box>
          {asset}
        </Typography>
      }
      toolTip={{
        placement: 'title',
        content: `Approximated ${asset} rewards`,
      }}
    />
  );
};

export const TOTAL_DEBT_REPAY_TITLE = 'TOTAL DEBT TO REPAY';

export const LabelTotalDebtRepay: React.FC<{ assetAmount: number }> = ({ assetAmount }) => {
  return (
    <LabelWithPrice
      labelProps={{
        variant: 'vertical',
        labelValueVariant: 'label1',
      }}
      asset={ENativeAsset.VST}
      labelTitle={TOTAL_DEBT_REPAY_TITLE}
      assetAmount={assetAmount}
    />
  );
};

export const LabelGovernanceStakingPenalty: FC<{
  penalty: number;
  labelTitle?: string;
  hasTooltip?: boolean;
  labelProps?: OptionalGenericInfoLabelProps;
  percent?: boolean;
}> = ({ penalty, labelProps, percent = true, hasTooltip = true, labelTitle = 'BVSTA PENALTY:' }) => {
  const hasPenalty = percent ? penalty < 0 : penalty > 0;
  if (percent) {
    return (
      <GenericInfoLabel
        labelTitle={labelTitle}
        labelValue={displayFormatUnits({ value: penalty, displayType: EDisplayType.Percent })}
        labelValueUnit={
          <VestaIcon
            size={24}
            noBg
            noBorder
            icon={hasPenalty ? VestaIcons.PENALTY_WARNING : VestaIcons.PENALTY_OKAY}
          />
        }
        toolTip={hasTooltip ? { content: 'Lorem Ipsum', placement: 'title' } : undefined}
        {...labelProps}
      />
    );
  } else {
    return (
      <GenericInfoLabel
        labelTitle={labelTitle}
        labelValue={`${displayFormatUnits({
          value: penalty,
          displayType: EDisplayType.Token,
        })}%`}
        labelValueUnit={
          <VestaIcon
            size={24}
            noBg
            noBorder
            icon={hasPenalty ? VestaIcons.PENALTY_WARNING : VestaIcons.PENALTY_OKAY}
          />
        }
        toolTip={{ content: 'Lorem Ipsum', placement: 'title' }}
      />
    );
  }
};

export const LabelWithPriceChangedStat: FC<{
  title: string;
  tooltip?: GenericInfoLabelToolTip;
  labelValueVariant?: string;
  asset: AllVestaAssets;
  currentValue: number;
  finalValue: number;
  invert?: boolean;
}> = ({ title, asset, labelValueVariant = 'label3', currentValue, finalValue, invert, tooltip }) => {
  const isIncreasing = finalValue > currentValue;
  const isEqual = finalValue === currentValue;
  const COLORS = [statusGreen['100'], primaryColors[100], statusRed['100']];
  const FINAL_VALUE_TEXT_COLOR = COLORS[!isEqual ? (isIncreasing && !invert ? 0 : 2) : 1];

  return (
    <Grid container direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
      <Grid item xs={5}>
        <LabelWithPrice
          labelProps={{
            variant: 'vertical',
            labelValueVariant: labelValueVariant,
            toolTip: tooltip,
          }}
          asset={asset}
          labelTitle={title}
          assetAmount={currentValue}
        />
      </Grid>
      <Grid container item xs={2} justifyContent={'center'}>
        <Box width={16} height={16}>
          <ArrowRight size={16} color="#b3b4c1" weight="bold" />
        </Box>
      </Grid>
      <Grid item xs={5}>
        <LabelWithPrice
          labelTitle="NEW STAKE"
          labelProps={{
            variant: 'vertical',
            labelValueVariant: labelValueVariant,
            align: 'right',
            labelValueSx: { transition: 'color 0.15s ease-in-out', color: FINAL_VALUE_TEXT_COLOR },
          }}
          asset={asset}
          assetAmount={finalValue}
          assetAmountColor={FINAL_VALUE_TEXT_COLOR}
        />
      </Grid>
    </Grid>
  );
};

export const LabelAutomateGelato: React.FC<{
  checked: boolean;
  handleChange: () => void;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ checked, handleChange, labelProps }) => {
  return (
    <Grid container sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
      <GenericInfoLabel
        labelTitle="MARKET PRICE:"
        labelValue={
          <Link href="https://z0r.de/1422">
            <Typography variant="label3">
              AUTOMATE BOOSTING USING{' '}
              <Box component="span" sx={{ color: secondaryColors[200] }}>
                GELATO
              </Box>
            </Typography>
          </Link>
        }
        sx={{ width: 'fit-content' }}
        {...labelProps}
      />
      <LabeledSwitch
        defaultChecked
        checked={checked}
        onChange={handleChange}
        onColor={secondaryColors[200]}
        offColor={primaryColors[700]}
        size="small"
      />
    </Grid>
  );
};

export const LabelBoostingAmount: React.FC<{
  boostingAmount: number;
  isBoost?: boolean;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ boostingAmount, isBoost = true, labelProps }) => (
  <LabelWithPrice
    asset={ENativeAsset.bVSTA}
    assetAmount={boostingAmount}
    labelTitle={isBoost ? 'BOOSTING:' : 'UNBOOSTING:'}
    labelProps={{
      variant: 'vertical',
      labelValueVariant: 'label2',
      toolTip: { content: 'Lorem ipsum is everywhere.', placement: 'title' },
      ...labelProps,
    }}
  />
);

export const LabelGetMoreLp: React.FC<{
  farm: BoostedFarm;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ farm, labelProps }) => (
  <GenericInfoLabel
    sx={{ width: 'fit-content' }}
    toolTip={{ content: 'Lorem ipsum', placement: 'value' }}
    labelValue={
      <Link href={TOKEN_BUY_LINKS[farm]} target="_blank">
        <Typography variant="label3" sx={{ color: secondaryColors[200] }}>
          GET MORE LP
        </Typography>
      </Link>
    }
    {...labelProps}
  />
);

export const LabelEarnBVsta: React.FC<{
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ labelProps }) => (
  <GenericInfoLabel
    sx={{ width: 'fit-content' }}
    toolTip={{ content: 'Lorem ipsum', placement: 'value' }}
    labelValue={
      <Link href="/coreModule" target="_blank">
        <Typography variant="label3" sx={{ color: secondaryColors[200] }}>
          {`EARN ${ENativeAsset.bVSTA}`}
        </Typography>
      </Link>
    }
    {...labelProps}
  />
);

export const LabelDepositOrWithdraw: React.FC<{
  asset: AllVestaAssets;
  assetAmount: number;
  isDeposit?: boolean;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({ asset, assetAmount, isDeposit = true, labelProps }) => (
  <LabelWithPrice
    asset={asset}
    assetAmount={assetAmount}
    labelTitle={isDeposit ? 'DEPOSITING:' : 'WITHDRAWING:'}
    labelProps={{
      variant: 'vertical',
      labelValueVariant: 'label2',
      toolTip: { content: 'Lorem ipsum is everywhere.', placement: 'title' },
      ...labelProps,
    }}
  />
);

export const LabelBoostedAmount: React.FC<{
  boostedAmount: number;
  variant?: LabelStatVariant;
  customTitle?: string;
  labelProps?: OptionalGenericInfoLabelProps;
}> = ({
  boostedAmount,
  variant,
  customTitle,
  labelProps = {
    align: 'start',
    variant: variant === 'header' ? 'vertical' : 'horizontal',
    labelValueVariant: variant === 'header' ? 'label2' : 'label3',
    toolTip: {
      content: 'Your boosted tokens',
      placement: variant === 'header' ? 'title' : 'value',
    },
  },
}) => (
  <LabelWithPrice
    labelProps={labelProps}
    labelTitle={customTitle ? customTitle : 'BOOSTED AMOUNT'}
    assetAmount={boostedAmount}
    asset={ENativeAsset.bVSTA}
  />
);
